import type { ForwardedRef } from "react";
import React, { forwardRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "goober";

import { EVENT_RENDER_ROUTE_COMPONENT } from "@/__main__/App.jsx";
import eventBus from "@/app/app-event-bus.mjs";
import SearchIcon from "@/inline-assets/search.svg";
import { setSearchQuery, showSearch } from "@/search/actions.mjs";
import { default as Clear } from "@/search/Clear.jsx";
import { DEFAULT_SEARCH_PLACEHOLDER } from "@/search/constants.mjs";
import searchRefs from "@/search/refs.mjs";
import { useClientOnly } from "@/util/ClientOnly.jsx";
import isMobile from "@/util/is-mobile.mjs";
import { useClientLayoutEffect } from "@/util/use-client-layout-effect.mjs";
import useForwardedRef from "@/util/use-forwarded-ref.mjs";
import { useSnapshot } from "@/util/use-snapshot.mjs";

const SearchIconBox = styled("div")`
  display: flex;
  align-items: center;
  justify-self: flex-start;
  padding-left: var(--sp-4);

  svg {
    width: var(--sp-6);
  }
`;

const ClearWrapper = styled("div")`
  display: flex;
  align-items: center;
  justify-self: flex-end;
  padding: var(--sp-2);
  opacity: 0;
  transition: opacity var(--transition);
  pointer-events: none;
`;

const SearchControl = styled("label", React.forwardRef)`
  display: grid;
  height: var(--sp-12);
  width: 42ch;
  border-radius: var(--br-lg);
  overflow: hidden;
  cursor: text;
  pointer-events: all;

  @container (inline-size <= 600px) {
    width: 32ch;
  }

  & > * {
    grid-area: 1 / 1;
  }

  input {
    max-width: 100%;
    padding: var(--sp-2) var(--sp-12);
    color: var(--shade1);
    background-color: var(--shade6);
    border-style: hidden;
    transition:
      background-color var(--transition),
      color var(--transition);

    &::placeholder {
      color: var(--shade2);
      font-size: var(--sp-3_5e);
      font-weight: 600;
    }

    & ~ ${SearchIconBox}, & ~ ${ClearWrapper} {
      color: var(--shade2);
    }

    & ~ ${ClearWrapper} label {
      background-color: var(--shade6);
    }

    &:active,
    &:focus {
      background-color: var(--shade0);
      color: var(--shade4);
      outline: none;

      & ~ ${ClearWrapper} label {
        background-color: var(--shade0);
      }
    }

    &:not(:active):not(:focus) {
      &:hover {
        background-color: var(--shade5);

        & ~ ${ClearWrapper} label {
          background-color: var(--shade5);
        }
      }
    }

    &:not(:placeholder-shown) ~ ${ClearWrapper} {
      opacity: 1;
      pointer-events: all;
    }
  }
`;

const SearchBar = (
  {
    id,
    placeholder,
    setFocusResult,
    setImFeelingLucky,
    ...restProps
  }: {
    id?: string;
    placeholder?: string;
    setFocusResult?: (value: boolean) => void;
    setImFeelingLucky?: (value: boolean) => void;
  } & JSX.IntrinsicElements["input"],
  ref: ForwardedRef<HTMLInputElement>,
) => {
  id ??= "search-input";

  const { t } = useTranslation();

  const { shouldShowGlobalSearch, globalSearchQuery } = useSnapshot(searchRefs);

  const inputRef = useForwardedRef(ref);

  useEffect(() => {
    if (!shouldShowGlobalSearch) return;

    inputRef.current?.focus();
  }, [shouldShowGlobalSearch, inputRef]);

  useEffect(() => {
    if (!inputRef.current) return;

    // Sync the search query with the text input's value
    inputRef.current.value = globalSearchQuery;
  }, [shouldShowGlobalSearch, globalSearchQuery, inputRef]);

  useClientLayoutEffect(() => {
    if (!shouldShowGlobalSearch) return;

    if (inputRef.current?.value.length > 0) {
      const onRouted = () => {
        setSearchQuery(inputRef.current?.value ?? "");
        showSearch();
      };

      // Omnisearch resets the search query when the route component is rendered, so we need to wait for that to happen
      eventBus.once(EVENT_RENDER_ROUTE_COMPONENT, onRouted);

      return () => {
        eventBus.off(EVENT_RENDER_ROUTE_COMPONENT, onRouted);
      };
    }
  }, [shouldShowGlobalSearch, inputRef]);

  const handleQueryEdit = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { selectionStart, selectionEnd, selectionDirection } = e.target;
      setSearchQuery(e.target.value, {
        selectionStart,
        selectionEnd,
        selectionDirection,
      });
      showSearch();
    },
    [],
  );

  const handleClearClicked = useCallback(() => {
    setSearchQuery("");
    inputRef.current?.focus();
  }, [inputRef]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        if (e.key === "ArrowRight") {
          // If the cursor was already at the end
          if (e.currentTarget.selectionStart === e.currentTarget.value.length) {
            e.preventDefault();

            setFocusResult?.(true);
          }
        } else if (e.key === "ArrowDown") {
          e.preventDefault();
          setFocusResult?.(true);
        }
      } else if (e.key === "Enter") {
        e.preventDefault();

        setFocusResult?.(false);
        setImFeelingLucky?.(true);
      } else {
        setFocusResult?.(false);
        setImFeelingLucky?.(false);
      }
    },
    [setFocusResult, setImFeelingLucky],
  );

  const isHydrated = useClientOnly();

  return (
    <SearchControl htmlFor={id}>
      <input
        id={id}
        ref={inputRef}
        type="text"
        placeholder={placeholder || t(...DEFAULT_SEARCH_PLACEHOLDER)}
        onChange={handleQueryEdit}
        onKeyDownCapture={onKeyDown}
        autoComplete="off"
        autoFocus={!isHydrated || !isMobile()}
        suppressHydrationWarning
        {...restProps}
      />

      <SearchIconBox>
        <SearchIcon />
      </SearchIconBox>

      <ClearWrapper className="clear-btn-box">
        <Clear
          onClick={handleClearClicked}
          label={t("common:search.clear", "Clear")}
        />
      </ClearWrapper>
    </SearchControl>
  );
};

export default forwardRef(SearchBar);
